import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {UserDefinedObjectTemplateAdditionalColumnType} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import type {
    KanbanBoardColumnConfig,
    KanbanCardSystemFieldId,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    kanbanBoardColumnConfigValidator,
    kanbanCardSystemFieldIdValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/KanbanBoardColumnConfig";
import {
    type OttopilotDataViewerConfig,
    ottopilotDataViewerConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotDataViewerConfig";
import {
    type SharedCalendarViewConfig,
    sharedCalendarViewConfigValidator,
} from "@vesta/vos-types/modules/workspaceLevel/domain/SharedCalendarViewConfig";
import {z as zod} from "zod";

export enum OttopilotCustomerAdditionalColumnType {
    email = "b4c86752-ea47-4172-b89e-d699b74e98ac",
    indianPhoneNumber = "b62677b6-5526-4617-b711-161625e85c2d",
    text = "beb4bf13-7c8b-4c2d-bcd0-32811ba491d2",
    unknown = "2bd7f243-1e76-48f0-9026-f1a5d558eb52",
    uuid = "9fbd6bb1-71b4-4f48-be61-8f77ed220a7a",
}

export enum OttopilotCustomerViewPanelSectionKind {
    columnGroup = "d5f19ba5-4cbc-46ef-bf31-64130c67b778",
    customerOverView = "e519278b-264d-4b92-8f50-4c43ede98268",
}

export type OttopilotCustomerAdditionalColumnResolver = {
    sql: string;
};

export type OttopilotCustomerTableViewColumn = {
    displayName: string;
    field: string;
    filteringCapability_contains: boolean;
    id: Uuid;
    sortingCapability: boolean;
} & (
    | {
          columnType: "OttopilotCustomer";
          isAdditionalColumn: true;
          type: OttopilotCustomerAdditionalColumnType;
      }
    | {
          columnType: "OttopilotEventContainer";
          isAdditionalColumn: true;
          type: UserDefinedObjectTemplateAdditionalColumnType;
      }
    | {
          columnType: "OttopilotCustomer";
          isAdditionalColumn: false;
          type?: never;
      }
    | {
          columnType: "OttopilotEventContainer";
          isAdditionalColumn: false;
          type?: never;
      }
);

export type OttopilotCustomerViewPanelColumn = {
    columnId: string;
    id: Uuid;
    isAdditionalColumn: boolean;
    label: string;
} & ({columnType: "OttopilotCustomer"} | {columnType: "OttopilotEventContainer"});

export type OttopilotCustomersWorkspaceCapabilityConfiguration = {
    additionalColumns: Array<
        {
            displayName: string;
            id: Uuid;
            resolver?: OttopilotCustomerAdditionalColumnResolver;
        } & (
            | {
                  configuration: {required: boolean};
                  type: OttopilotCustomerAdditionalColumnType.email;
              }
            | {
                  configuration: {required: boolean};
                  type: OttopilotCustomerAdditionalColumnType.indianPhoneNumber;
              }
            | {
                  configuration: {required: boolean};
                  type: OttopilotCustomerAdditionalColumnType.text;
              }
            | {
                  configuration: Record<string, never>;
                  type: OttopilotCustomerAdditionalColumnType.unknown;
              }
            | {
                  configuration: {required: boolean};
                  type: OttopilotCustomerAdditionalColumnType.uuid;
              }
        )
    >;
    customerPageConfig: {
        callButtonColumnId: Uuid | null;
        calendarConfig?: SharedCalendarViewConfig & {
            calendarStartDateColumnId?: Uuid;
            calendarTitleOttopilotStringTemplate?: string;
            eventTypeSource?: {
                columnId: Uuid | "disposition" | "status";
                columnType: "OttopilotCustomer";
            };
        };
        kanbanBoardConfig?: {
            kanbanBoardCardView: {
                details: Array<
                    Array<
                        {
                            iconId: Uuid;
                            label: string;
                        } & (
                            | {
                                  dataSource: Uuid | KanbanCardSystemFieldId;
                                  type: "SingleColumn";
                              }
                            | {
                                  dataSource: Array<Uuid | KanbanCardSystemFieldId>;
                                  seperator: string;
                                  type: "MultipleColumn";
                              }
                        )
                    >
                >;
                heading: Array<Uuid | KanbanCardSystemFieldId>;
            };
            kanbanBoardPageView: {
                columnConfig: Array<KanbanBoardColumnConfig>;
                filterFieldId: string;
            };
        };
        ottopilotDataViewerConfig?: OttopilotDataViewerConfig;
        tableViewColumns?: Array<OttopilotCustomerTableViewColumn>;
        viewPanel?: {
            sections: Array<
                | {
                      className?: string;
                      columns: Array<OttopilotCustomerViewPanelColumn>;
                      displayName: string;
                      iconBackgroundColor?: string;
                      iconColor?: string;
                      iconId?: Uuid;
                      id: Uuid;
                      initialIsOpen?: boolean;
                      kind: OttopilotCustomerViewPanelSectionKind.columnGroup;
                  }
                | {
                      className?: string;
                      displayName?: string;
                      iconBackgroundColor?: string;
                      iconColor?: string;
                      iconId?: Uuid;
                      id: Uuid;
                      initialIsOpen?: boolean;
                      kind: OttopilotCustomerViewPanelSectionKind.customerOverView;
                  }
            >;
        };
        whatsAppButtonColumnId: Uuid | null;
    };
    displayNameOttopilotStringTemplate: string;
    dispositions: Array<{
        displayName: string;
        id: Uuid;
    }>;
    moduleDisplayName: {
        pluralLowerCase: string;
        pluralSentenceCase: string;
        singularLowerCase: string;
        singularSentenceCase: string;
    };
};

const customerAdditionalColumnBaseValidator = zod.strictObject({
    displayName: zod.string(),
    id: uuidZodValidator,
    resolver: zod.strictObject({
        sql: zod.string().refine((value) => value.trim() !== "", "resolver.sql cannot be blank"),
    }).optional(),
});

const customerAdditionalColumnRequiredConfigurationValidator = zod.strictObject({
    required: zod.boolean(),
});

const customerAdditionalColumnValidator = zod.discriminatedUnion("type", [
    customerAdditionalColumnBaseValidator.extend({
        configuration: customerAdditionalColumnRequiredConfigurationValidator,
        type: zod.literal(OttopilotCustomerAdditionalColumnType.email),
    }),
    customerAdditionalColumnBaseValidator.extend({
        configuration: customerAdditionalColumnRequiredConfigurationValidator,
        type: zod.literal(OttopilotCustomerAdditionalColumnType.indianPhoneNumber),
    }),
    customerAdditionalColumnBaseValidator.extend({
        configuration: customerAdditionalColumnRequiredConfigurationValidator,
        type: zod.literal(OttopilotCustomerAdditionalColumnType.text),
    }),
    customerAdditionalColumnBaseValidator.extend({
        configuration: zod.strictObject({}),
        type: zod.literal(OttopilotCustomerAdditionalColumnType.unknown),
    }),
    customerAdditionalColumnBaseValidator.extend({
        configuration: customerAdditionalColumnRequiredConfigurationValidator,
        type: zod.literal(OttopilotCustomerAdditionalColumnType.uuid),
    }),
]);

const moduleDisplayNameValidator = zod.strictObject({
    pluralLowerCase: zod.string(),
    pluralSentenceCase: zod.string(),
    singularLowerCase: zod.string(),
    singularSentenceCase: zod.string(),
});

const customerTableViewColumnBaseValidator = zod.strictObject({
    displayName: zod.string(),
    field: zod.string(),
    filteringCapability_contains: zod.boolean(),
    id: uuidZodValidator,
    sortingCapability: zod.boolean(),
});

const customerTableViewColumnValidator: zod.ZodType<OttopilotCustomerTableViewColumn> = zod.union([
    customerTableViewColumnBaseValidator.extend({
        columnType: zod.literal("OttopilotCustomer"),
        isAdditionalColumn: zod.literal(true),
        type: zod.enum(OttopilotCustomerAdditionalColumnType),
    }),
    customerTableViewColumnBaseValidator.extend({
        columnType: zod.literal("OttopilotEventContainer"),
        isAdditionalColumn: zod.literal(true),
        type: zod.enum(UserDefinedObjectTemplateAdditionalColumnType),
    }),
    customerTableViewColumnBaseValidator.extend({
        columnType: zod.literal("OttopilotCustomer"),
        isAdditionalColumn: zod.literal(false),
    }),
    customerTableViewColumnBaseValidator.extend({
        columnType: zod.literal("OttopilotEventContainer"),
        isAdditionalColumn: zod.literal(false),
    }),
]);

const customerViewPanelColumnValidator: zod.ZodType<OttopilotCustomerViewPanelColumn> = zod.union([
    zod.strictObject({
        columnId: zod.string(),
        columnType: zod.literal("OttopilotCustomer"),
        id: uuidZodValidator,
        isAdditionalColumn: zod.boolean(),
        label: zod.string(),
    }),
    zod.strictObject({
        columnId: zod.string(),
        columnType: zod.literal("OttopilotEventContainer"),
        id: uuidZodValidator,
        isAdditionalColumn: zod.boolean(),
        label: zod.string(),
    }),
]);

const customerViewPanelSectionPresentationValidator = zod.strictObject({
    className: zod.string().optional(),
    iconBackgroundColor: zod.string().optional(),
    iconColor: zod.string().optional(),
    iconId: uuidZodValidator.optional(),
    id: uuidZodValidator,
    initialIsOpen: zod.boolean().optional(),
});

const customerViewPanelValidator = zod.strictObject({
    sections: zod
        .union([
            customerViewPanelSectionPresentationValidator.extend({
                columns: customerViewPanelColumnValidator.array(),
                displayName: zod.string(),
                kind: zod.literal(OttopilotCustomerViewPanelSectionKind.columnGroup),
            }),
            customerViewPanelSectionPresentationValidator.extend({
                displayName: zod.string().optional(),
                kind: zod.literal(OttopilotCustomerViewPanelSectionKind.customerOverView),
            }),
        ])
        .array(),
});

const kanbanCardDataSourceValidator = zod.union([uuidZodValidator, kanbanCardSystemFieldIdValidator]);

const customerKanbanBoardCardViewValidator = zod.strictObject({
    details: zod.array(
        zod.array(
            zod.discriminatedUnion("type", [
                zod.strictObject({
                    dataSource: kanbanCardDataSourceValidator,
                    iconId: uuidZodValidator,
                    label: zod.string(),
                    type: zod.literal("SingleColumn"),
                }),
                zod.strictObject({
                    dataSource: kanbanCardDataSourceValidator.array(),
                    iconId: uuidZodValidator,
                    label: zod.string(),
                    seperator: zod.string(),
                    type: zod.literal("MultipleColumn"),
                }),
            ]),
        ),
    ),
    heading: kanbanCardDataSourceValidator.array(),
});

const customerCalendarConfigValidator = sharedCalendarViewConfigValidator.extend({
    calendarStartDateColumnId: uuidZodValidator.optional(),
    calendarTitleOttopilotStringTemplate: zod.string().optional(),
    eventTypeSource: zod
        .strictObject({
            columnId: zod.union([uuidZodValidator, zod.literal("disposition"), zod.literal("status")]),
            columnType: zod.literal("OttopilotCustomer"),
        })
        .optional(),
});

const customerPageConfigValidator = zod.strictObject({
    callButtonColumnId: uuidZodValidator.nullable(),
    calendarConfig: customerCalendarConfigValidator.optional(),
    kanbanBoardConfig: zod
        .strictObject({
            kanbanBoardCardView: customerKanbanBoardCardViewValidator,
            kanbanBoardPageView: zod.strictObject({
                columnConfig: kanbanBoardColumnConfigValidator.array(),
                filterFieldId: zod.string(),
            }),
        })
        .optional(),
    ottopilotDataViewerConfig: ottopilotDataViewerConfigValidator.optional(),
    tableViewColumns: customerTableViewColumnValidator.array().optional(),
    viewPanel: customerViewPanelValidator.optional(),
    whatsAppButtonColumnId: uuidZodValidator.nullable(),
});

export const ottopilotCustomersWorkspaceCapabilityConfigurationValidator: zod.ZodType<OttopilotCustomersWorkspaceCapabilityConfiguration> = zod.strictObject({
    additionalColumns: customerAdditionalColumnValidator.array(),
    customerPageConfig: customerPageConfigValidator,
    displayNameOttopilotStringTemplate: zod.string(),
    dispositions: zod
        .strictObject({
            displayName: zod.string(),
            id: uuidZodValidator,
        })
        .array(),
    moduleDisplayName: moduleDisplayNameValidator,
});
